'use client';

import {
    isSilencedExternalRuntimeNoiseConsoleError,
    isSilencedExternalRuntimeNoiseError,
    isSilencedNextRscConsoleError,
    isSilencedOidcDatadogEvent,
} from '@/lib/error-silencing';
import type { DatadogLogs } from '@datadog/browser-logs';
import type { DatadogRum, User as DatadogRumUser } from '@datadog/browser-rum';

type DatadogSdk = {
    logs: DatadogLogs;
    rum: DatadogRum;
};

const getRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const getString = (value: unknown): string => (typeof value === 'string' ? value : '');

const getNumeric = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

const getBoolean = (value: unknown): boolean | null => (typeof value === 'boolean' ? value : null);

const shouldDropRscAbortNetworkEvent = (event: unknown, context?: unknown): boolean => {
    const eventRecord = getRecord(event);
    if (!eventRecord) {
        return false;
    }

    const http = getRecord(eventRecord.http);
    const resource = getRecord(eventRecord.resource);
    const error = getRecord(eventRecord.error);

    const message = getString(eventRecord.message).toLowerCase();
    const stack = getString(error?.stack).toLowerCase();
    const httpUrl = getString(http?.url).toLowerCase();
    const resourceUrl = getString(resource?.url).toLowerCase();
    const contextRecord = getRecord(context);
    const isContextAborted = getBoolean(contextRecord?.isAborted) === true;

    const hasRscQuery =
        message.includes('_rsc=') ||
        httpUrl.includes('_rsc=') ||
        resourceUrl.includes('_rsc=') ||
        message.includes('fetch error get');
    const hasAbortSignal = stack.includes('aborterror') || stack.includes('signal is aborted without reason');
    const isStatusZero = getNumeric(http?.status_code) === 0 || getNumeric(resource?.status_code) === 0;

    return hasRscQuery && (hasAbortSignal || isContextAborted || isStatusZero);
};

const shouldDropGraphQlNetworkFailureEvent = (event: unknown): boolean => {
    const eventRecord = getRecord(event);
    if (!eventRecord) {
        return false;
    }

    const http = getRecord(eventRecord.http);
    const resource = getRecord(eventRecord.resource);
    const error = getRecord(eventRecord.error);

    const message = getString(eventRecord.message).toLowerCase();
    const stack = getString(error?.stack).toLowerCase();
    const httpUrl = getString(http?.url).toLowerCase();
    const resourceUrl = getString(resource?.url).toLowerCase();
    const method = getString(http?.method).toLowerCase();

    const isGraphQlUrl = httpUrl.includes('/graphql') || resourceUrl.includes('/graphql');
    const isGraphQlPost = method === 'post' || message.includes('fetch error post');
    const isStatusZero = getNumeric(http?.status_code) === 0 || getNumeric(resource?.status_code) === 0;
    const hasNetworkFailureSignature =
        stack.includes('load failed') ||
        stack.includes('failed to fetch') ||
        stack.includes('networkerror') ||
        message.includes('load failed') ||
        message.includes('failed to fetch');

    return isGraphQlUrl && isGraphQlPost && isStatusZero && hasNetworkFailureSignature;
};

const shouldDropDatadogEvent = (event: unknown, context?: unknown): boolean =>
    shouldDropRscAbortNetworkEvent(event, context) ||
    shouldDropGraphQlNetworkFailureEvent(event) ||
    isSilencedExternalRuntimeNoiseError(event) ||
    isSilencedOidcDatadogEvent(event);

declare global {
    interface Window {
        __bunqerConsoleErrorPatched?: boolean;
        __bunqerDatadogInitPromise?: Promise<void>;
        __bunqerDatadogInitialized?: boolean;
        __bunqerDatadogSdkPromise?: Promise<DatadogSdk | null>;
        __bunqerErrorSilencingListenersAttached?: boolean;
    }
}

const hasDatadogConfig = (): boolean =>
    Boolean(process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN && process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID);

const attachErrorSilencing = () => {
    if (typeof window === 'undefined' || !hasDatadogConfig()) {
        return;
    }

    if (!window.__bunqerConsoleErrorPatched) {
        const originalConsoleError = console.error;
        console.error = (...args) => {
            if (isSilencedNextRscConsoleError(args) || isSilencedExternalRuntimeNoiseConsoleError(args)) {
                return;
            }

            originalConsoleError(...args);
        };
        window.__bunqerConsoleErrorPatched = true;
    }

    if (!window.__bunqerErrorSilencingListenersAttached) {
        window.addEventListener('unhandledrejection', (event) => {
            if (isSilencedExternalRuntimeNoiseError(event.reason)) {
                event.preventDefault();
            }
        });

        window.addEventListener('error', (event) => {
            const errorCandidate = event.error ?? event.message;
            if (isSilencedExternalRuntimeNoiseError(errorCandidate)) {
                event.preventDefault();
            }
        });

        window.__bunqerErrorSilencingListenersAttached = true;
    }
};

const loadDatadogSdk = (): Promise<DatadogSdk | null> => {
    if (typeof window === 'undefined' || !hasDatadogConfig()) {
        return Promise.resolve(null);
    }

    if (!window.__bunqerDatadogSdkPromise) {
        window.__bunqerDatadogSdkPromise = Promise.all([
            import('@datadog/browser-logs'),
            import('@datadog/browser-rum'),
        ])
            .then(([logsModule, rumModule]) => ({
                logs: logsModule.datadogLogs,
                rum: rumModule.datadogRum,
            }))
            .catch((error) => {
                window.__bunqerDatadogSdkPromise = undefined;
                throw error;
            });
    }

    return window.__bunqerDatadogSdkPromise;
};

export const initDatadog = (): Promise<void> => {
    if (typeof window === 'undefined' || !hasDatadogConfig()) {
        return Promise.resolve();
    }

    attachErrorSilencing();

    if (window.__bunqerDatadogInitialized) {
        return Promise.resolve();
    }

    if (!window.__bunqerDatadogInitPromise) {
        window.__bunqerDatadogInitPromise = loadDatadogSdk()
            .then((sdk) => {
                if (!sdk || window.__bunqerDatadogInitialized) {
                    return;
                }

                sdk.logs.init({
                    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '',
                    site: 'datadoghq.eu',
                    service: 'frontend',
                    env: process.env.NEXT_PUBLIC_ENV || 'production',
                    version: process.env.NEXT_PUBLIC_APP_VERSION || '0.0.0',
                    forwardErrorsToLogs: true,
                    forwardConsoleLogs: ['error', 'warn'],
                    forwardReports: ['intervention'],
                    useSecureSessionCookie: true,
                    usePartitionedCrossSiteSessionCookie: true,
                    sessionPersistence: 'local-storage',
                    beforeSend: (event: unknown, context: unknown) => !shouldDropDatadogEvent(event, context),
                });

                sdk.rum.init({
                    applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID || '',
                    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '',
                    site: 'datadoghq.eu',
                    service: 'frontend',
                    env: process.env.NEXT_PUBLIC_ENV || 'production',
                    version: process.env.NEXT_PUBLIC_APP_VERSION || '0.0.0',
                    sessionSampleRate: 100,
                    sessionReplaySampleRate: 100,
                    defaultPrivacyLevel: 'allow',
                    trackResources: true,
                    trackResourceHeaders: true,
                    trackLongTasks: true,
                    trackUserInteractions: true,
                    compressIntakeRequests: true,
                    allowedTracingUrls: [
                        {
                            match: (url: string) => url.startsWith(process.env.NEXT_PUBLIC_API_URL || ''),
                            propagatorTypes: ['tracecontext', 'datadog'],
                        },
                    ],
                    traceSampleRate: 100,
                    traceContextInjection: 'sampled',
                    allowedGraphQlUrls: [
                        {
                            match: (url: string) => url.startsWith(process.env.NEXT_PUBLIC_API_URL || ''),
                            trackPayload: true,
                            trackResponseErrors: true,
                        },
                    ],
                    useSecureSessionCookie: true,
                    usePartitionedCrossSiteSessionCookie: true,
                    sessionPersistence: 'local-storage',
                    beforeSend: (event: unknown, context: unknown) => !shouldDropDatadogEvent(event, context),
                });

                sdk.rum.startSessionReplayRecording();
                window.__bunqerDatadogInitialized = true;
            })
            .finally(() => {
                window.__bunqerDatadogInitPromise = undefined;
            });
    }

    return window.__bunqerDatadogInitPromise ?? Promise.resolve();
};

export const clearDatadogUser = async () => {
    await initDatadog();

    const sdk = await loadDatadogSdk();
    sdk?.rum.clearUser();
};

export const setDatadogUser = async (user: DatadogRumUser) => {
    await initDatadog();

    const sdk = await loadDatadogSdk();
    sdk?.rum.setUser(user);
};
